import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../api';
import { DateOnlyInput } from '../components/DateOnlyInput';
export function OperationsFindingReview({ data, onSaved, disabled }: { data: any; onSaved: () => Promise<unknown>; disabled: boolean }) {
  const a = data.assignment,
    [summary, setSummary] = useState<any>(),
    [message, setMessage] = useState(''),
    [busy, setBusy] = useState(false),
    [scope, setScope] = useState<string[]>([]);
  const retry = useRef<{ body: string; key: string } | undefined>(undefined),
    scopeId = useRef(crypto.randomUUID());
  const load = async () => setSummary(await api('/api/operations/visit-reports/' + a.id + '/follow-ups'));
  useEffect(() => {
    void load().catch((e) => {
      if (e.statusCode !== 403) setMessage(e.message);
    });
  }, [a.id, a.version]);
  const latest = data.submissions[0],
    exceptions = latest ? a.definition.questions.filter((q: any) => ['Fail', 'Unable to check', 'Unable to read'].includes(latest.answers[q.id]?.value)) : [];
  async function save(path: string, body: any) {
    setBusy(true);
    setMessage('');
    try {
      const content = { expectedVersion: a.version, ...body },
        text = path + JSON.stringify(content);
      if (retry.current?.body !== text) retry.current = { body: text, key: crypto.randomUUID() };
      await api('/api/operations/visit-reports/' + a.id + '/' + path, { method: 'POST', body: JSON.stringify({ ...content, idempotencyKey: retry.current.key }) });
      retry.current = undefined;
      await onSaved();
      await load();
      setMessage('Saved. Original evidence and source requirements remain available.');
    } catch (e: any) {
      setMessage(e.message);
    } finally {
      setBusy(false);
    }
  }
  if (!summary && !message) return null;
  return (
    <section className="report-no-print" aria-label="Operations follow-up">
      <h2>Findings and return work</h2>
      <Link to={'/operations/visits/' + data.operationsVisitId + '?building=' + a.development_id}>Open attendance</Link>
      {message && <p role="status">{message}</p>}
      {summary && (
        <>
          <p>Review each report and resolve outstanding items before completing the source activity. Use the usual urgent response for safety concerns.</p>
          {summary.followUps.map((f: any) => (
            <p key={f.id}>
              <Link to={(f.action_id ? '/work/actions/' + f.action_id : '/work/cases/' + f.work_id) + '?building=' + a.development_id}>
                {f.purpose === 'Access' ? 'Access resolution Action' : 'Repairs Work'}
              </Link>{' '}
              · {f.action_status ?? f.work_status}
            </p>
          ))}
          {exceptions.map((q: any) => {
            const unable = String(latest.answers[q.id]?.value).startsWith('Unable');
            return (
              <article key={q.id}>
                <h3>{q.label}</h3>
                {(unable ? summary.canAccess : summary.canDefect) && (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      const f = new FormData(e.currentTarget);
                      void save('follow-ups', {
                        submissionId: latest.id,
                        questionId: q.id,
                        purpose: unable ? 'Access' : 'Defect',
                        reason: f.get('reason'),
                        ...(unable ? { assignedTo: f.get('owner'), dueOn: f.get('dueOn') } : { workId: f.get('workId') || null, priority: f.get('priority') })
                      });
                    }}
                  >
                    <label>
                      {unable ? 'Access action note' : 'Defect handoff note'}
                      <input name="reason" minLength={3} maxLength={1000} required />
                    </label>
                    {unable ? (
                      <>
                        <label>
                          Access action owner
                          <select name="owner" required>
                            <option value="">Choose the responsible person</option>
                            {summary.owners.map((u: any) => (
                              <option key={u.id} value={u.id}>
                                {u.name}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          Access action due
                          <DateOnlyInput name="dueOn" required />
                        </label>
                      </>
                    ) : (
                      <>
                        <label>
                          Repairs Work
                          <select name="workId">
                            <option value="">Create new Repairs Work</option>
                            {data.workOptions.map((w: any) => (
                              <option key={w.id} value={w.id}>
                                {w.title}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          Finding priority
                          <select name="priority">
                            {['Normal', 'Low', 'High', 'Urgent'].map((p) => (
                              <option key={p}>{p}</option>
                            ))}
                          </select>
                        </label>
                      </>
                    )}
                    <button disabled={busy || disabled}>{unable ? 'Create access Action' : 'Link defect to Repairs Work'}</button>
                  </form>
                )}
              </article>
            );
          })}
          {summary.canAuthorise && a.status === 'Accepted' && exceptions.length > 0 && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void save('continuations', { id: scopeId.current, submissionId: latest.id, questionIds: scope, reason: new FormData(e.currentTarget).get('reason') });
              }}
            >
              <h3>Authorise remaining checks</h3>
              <p>Select exact failed or unable independent check items. The return uses the original method and a separately checked officer and time.</p>
              {exceptions
                .filter((q: any) => q.type === 'check' && !q.condition)
                .map((q: any) => (
                  <label key={q.id}>
                    <input
                      type="checkbox"
                      checked={scope.includes(q.id)}
                      onChange={(e) => setScope((old) => (e.target.checked ? [...old, q.id] : old.filter((k) => k !== q.id)))}
                    />
                    {q.label}
                  </label>
                ))}
              <label>
                Remaining scope basis
                <input name="reason" minLength={3} maxLength={1000} required />
              </label>
              <button disabled={busy || disabled || !scope.length}>Authorise return checks</button>
            </form>
          )}
          {summary.continuations.map((c: any) => (
            <article key={c.id}>
              <h3>Authorised return</h3>
              {c.report_id ? (
                <>
                  <Link to={'/reports/' + c.report_id + '?building=' + a.development_id}>Open return report</Link> · {c.status}
                  {data.canReview &&
                    c.status === 'Accepted' &&
                    c.question_ids.map((key: string) => (
                      <form
                        key={key}
                        onSubmit={(e) => {
                          e.preventDefault();
                          void save('resolutions', {
                            scopeId: c.id,
                            questionId: key,
                            continuationSubmission: c.submission_id,
                            expectedContinuationVersion: c.report_version,
                            reason: new FormData(e.currentTarget).get('reason')
                          });
                        }}
                      >
                        <label>
                          Acceptance basis for {key}
                          <input name="reason" required minLength={3} maxLength={1000} />
                        </label>
                        <button disabled={busy || disabled}>Resolve original {key} from accepted return</button>
                      </form>
                    ))}
                </>
              ) : (
                <Link to={'/operations/visits?building=' + a.development_id}>Schedule the authorised return</Link>
              )}
            </article>
          ))}
          {summary.isContinuation && data.canComplete && ['Submitted', 'Accepted', 'Returned'].includes(a.status) && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const f = new FormData(e.currentTarget);
                void save('effort', { expectedEffortVersion: summary.effort?.version ?? 0, actualMinutes: Number(f.get('minutes')), actualOn: f.get('actualOn') });
              }}
            >
              <h3>Actual return effort</h3>
              <label>
                Actual return minutes
                <input name="minutes" type="number" required min={0} max={10080} defaultValue={summary.effort?.actual_minutes ?? ''} />
              </label>
              <label>
                Actual return date
                <DateOnlyInput name="actualOn" required defaultValue={summary.effort?.actual_on ?? ''} />
              </label>
              <button disabled={busy || disabled}>Save actual return effort</button>
            </form>
          )}
        </>
      )}
    </section>
  );
}
